import {
    GET_PROPOSAL_LIST_REQUEST,
    GET_PROPOSAL_LIST_SUCCESS,
    GET_PROPOSAL_LIST_FAILURE,
    GET_PROPOSAL_LIST,
    TOGGLE_PROPOSAL_LIST_DRAWER,
    GET_PROPOSAL_PREVIEW_REQUEST,
    GET_PROPOSAL_PREVIEW_SUCCESS,
    GET_PROPOSAL_PREVIEW_FAILURE,
    CREATE_PROPOSAL,
    CREATE_PROPOSAL_REQUEST,
    CREATE_PROPOSAL_SUCCESS,
    CREATE_PROPOSAL_FAILURE,
    DELETE_PROPOSAL_FAILURE,
    DELETE_PROPOSAL_REQUEST,
    DELETE_PROPOSAL_SUCCESS,
    DELETE_PROPOSAL,
    COPY_PROPOSAL,
    COPY_PROPOSAL_FAILURE,
    COPY_PROPOSAL_REQUEST,
    COPY_PROPOSAL_SUCCESS,
    UPDATE_PROPOSAL_FAILURE,
    UPDATE_PROPOSAL_REQUEST,
    UPDATE_PROPOSAL_SUCCESS,
    SET_IS_PRIMARY_PROPOSAL,
    GET_PROPOSAL,
    GET_PROPOSAL_FAILURE,
    GET_PROPOSAL_REQUEST,
    GET_PROPOSAL_SUCCESS,
    UPDATE_PROPOSAL,
    PAYMENT_GRID_DOWN_PAYMENT_CHECKBOX_CLICKED,
    PAYMENT_GRID_TERM_CHECKBOX_CLICKED
} from '../actionTypes/proposalActionTypes';
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import { RSAA } from 'redux-api-middleware';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { CustomizablePdfPayload } from '@makemydeal/dr-platform-types';
import type { ScopeGridSelections } from '../types/types';

export const getProposalList = (proposalType?: string) => {
    return {
        type: GET_PROPOSAL_LIST,
        payload: { proposalType }
    };
};

export const getProposalListRSAA = (dealerId: number, bffEndpointConfig: IConfigObj, proposalType?: string) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/proposals`;
    const url = new URL(endpoint);
    if (proposalType) {
        url.searchParams.set('type', proposalType);
    }

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [GET_PROPOSAL_LIST_REQUEST, GET_PROPOSAL_LIST_SUCCESS, GET_PROPOSAL_LIST_FAILURE],
            // Overriding the fetch function to ensure compatibility with the module's context.
            // When working with a federated module, the global `fetch` instance (window.fetch) may differ from the host application's instance.
            // This can cause issues with interceptors
            fetch: async (...args) => {
                return await fetch(...args);
            }
        } satisfies RSAACallRequest
    };
};

export const getProposal = (proposalId: string) => {
    return {
        type: GET_PROPOSAL,
        payload: { proposalId }
    };
};

export const getProposalRSAA = (dealerId: number, proposalId: number, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const proposalIdEscaped = encodeURIComponent(proposalId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/proposals/${proposalIdEscaped}`;
    const url = new URL(endpoint);

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [GET_PROPOSAL_REQUEST, GET_PROPOSAL_SUCCESS, GET_PROPOSAL_FAILURE],
            // Overriding the fetch function to ensure compatibility with the module's context.
            // When working with a federated module, the global `fetch` instance (window.fetch) may differ from the host application's instance.
            // This can cause issues with interceptors
            fetch: async (...args) => {
                return await fetch(...args);
            }
        } satisfies RSAACallRequest
    };
};

export const toggleProposalListDrawer = (show: boolean) => ({
    type: TOGGLE_PROPOSAL_LIST_DRAWER,
    payload: { show }
});

export const getProposalPreview = (proposalId?: string, selectedScopeIds?: string[], scopeGridSelections?: ScopeGridSelections) => {
    return {
        type: 'GET_PROPOSAL_PREVIEW',
        payload: { proposalId, selectedScopeIds, scopeGridSelections }
    };
};

export const getProposalPreviewRSAA = (pdfPayload: CustomizablePdfPayload, bffEndpointConfig: IConfigObj) => {
    return {
        [RSAA]: {
            endpoint: new URL(`${buildFromConfig(bffEndpointConfig, '')}/proposal/preview`).toString(),
            method: 'POST',
            body: JSON.stringify(pdfPayload),
            headers: { 'Content-Type': 'application/json' },
            types: [GET_PROPOSAL_PREVIEW_REQUEST, GET_PROPOSAL_PREVIEW_SUCCESS, GET_PROPOSAL_PREVIEW_FAILURE],
            // Overriding the fetch function to ensure compatibility with the module's context.
            // When working with a federated module, the global `fetch` instance (window.fetch) may differ from the host application's instance.
            // This can cause issues with interceptors
            fetch: async (...args) => {
                return await fetch(...args);
            }
        } satisfies RSAACallRequest
    };
};

export const createProposal = (proposalData: any) => ({
    type: CREATE_PROPOSAL,
    payload: { proposalData }
});

export const createProposalRSAA = (proposalData: any, bffEndpointConfig: IConfigObj, dealerId: number) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const endpoint = `${proxyUrl}/dealer/${dealerId}/proposals`;
    const url = new URL(endpoint);

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(proposalData),
            types: [CREATE_PROPOSAL_REQUEST, CREATE_PROPOSAL_SUCCESS, CREATE_PROPOSAL_FAILURE]
        } satisfies RSAACallRequest
    };
};

export const deleteProposal = (proposalId: string) => ({
    type: DELETE_PROPOSAL,
    payload: { proposalId }
});

export const deleteProposalRSAA = (bffEndpointConfig: IConfigObj, dealerId: number, proposalId: string) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const proposalIdEscaped = encodeURIComponent(proposalId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/proposals/${proposalIdEscaped}`;
    const url = new URL(endpoint);

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            types: [DELETE_PROPOSAL_REQUEST, DELETE_PROPOSAL_SUCCESS, DELETE_PROPOSAL_FAILURE]
        } satisfies RSAACallRequest
    };
};

export const setIsPrimaryProposal = (proposalId: string) => ({
    type: SET_IS_PRIMARY_PROPOSAL,
    payload: { proposalId }
});

export const copyProposalRSAA = (bffEndpointConfig: IConfigObj, dealerId: number, proposalId: string) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const proposalIdEscaped = encodeURIComponent(proposalId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/proposals/${proposalIdEscaped}/copy`;
    const url = new URL(endpoint);

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            types: [COPY_PROPOSAL_REQUEST, COPY_PROPOSAL_SUCCESS, COPY_PROPOSAL_FAILURE]
        } satisfies RSAACallRequest
    };
};

export const copyProposal = (proposalId: string) => ({
    type: COPY_PROPOSAL,
    payload: { proposalId }
});

export const updateProposalRSAA = (
    proposalData: any,
    bffEndpointConfig: IConfigObj,
    dealerId: number,
    proposalId: string,
    onSuccessMeta?: { withListFetch: boolean }
) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const dealerIdEscaped = encodeURIComponent(dealerId);
    const proposalIdEscaped = encodeURIComponent(proposalId);
    const endpoint = `${proxyUrl}/dealer/${dealerIdEscaped}/proposals/${proposalIdEscaped}`;
    const url = new URL(endpoint);

    return {
        [RSAA]: {
            endpoint: url.toString(),
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(proposalData),
            types: [UPDATE_PROPOSAL_REQUEST, { type: UPDATE_PROPOSAL_SUCCESS, meta: onSuccessMeta }, UPDATE_PROPOSAL_FAILURE]
        } satisfies RSAACallRequest
    };
};

export const updateProposal = (proposalData: any, proposalId: string) => ({
    type: UPDATE_PROPOSAL,
    payload: { proposalData, proposalId }
});

// Analytics-only click events for the Print Proposal payment grid (consumed by cdlV3AnalyticsMiddleware).
export const paymentGridDownPaymentCheckboxClicked = () => ({
    type: PAYMENT_GRID_DOWN_PAYMENT_CHECKBOX_CLICKED
});

export const paymentGridTermCheckboxClicked = () => ({
    type: PAYMENT_GRID_TERM_CHECKBOX_CLICKED
});
